// =============================================================================
// ui/shell/RangePicker.tsx — the global date-range control.
// -----------------------------------------------------------------------------
// One control, in the toolbar, setting the scope every tab reads. The old client
// puts it in the header for the same reason: a per-tab date filter is how two
// screens come to disagree about what "this week" covered.
//
// ⚠ STRUCTURE, NOT THE DESIGN SYSTEM. The old client uses a dropdown button with
// an inline single-calendar range picker. This is a `<select>` plus two native
// date inputs — same states, same semantics, no custom calendar. The visual
// parity pass is a separate relocation (see AppShell's header note); building a
// bespoke calendar widget by eye now would have to be redone against the real
// tokens later.
//
// The one thing it does NOT compromise on is the WEEK HINT, which is mode-
// dependent: session mode's week ends Friday, because there are no weekend
// sessions. A fixed "Mon–Sun" label would be wrong for exactly the users who
// turned session mode on.
// =============================================================================

import { useRange } from '../state/range';
import { RANGE_PRESETS } from '../../selectors/range';
import type { RangePreset, RangeSelection } from '../../selectors/range';

/**
 * Labels transcribed from the old client's DATE_RANGE_PRESETS.
 *
 * ⚠ Keyed off `RANGE_PRESETS` rather than repeating the list, so the picker and
 * the validator cannot drift. A preset added to the domain but missing a label
 * here is a type error, not a blank option.
 */
const LABELS: Record<RangePreset, string> = {
  all: 'All time',
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  month: 'This month',
  year: 'This year',
  custom: 'Custom range',
};

export function RangePicker() {
  // Thin wrapper kept so the toolbar and the drawer share one implementation.
  return <div className="range-picker" data-testid="range-picker"><RangeControls /></div>;
}

/** The controls themselves, embedded by the Filters drawer’s Date section. */
export function RangeControls() {
  const { selection, mode, label, setRange } = useRange();

  const hint = (id: RangePreset): string => {
    if (id === 'week') return mode === 'session' ? ' (Mon–Fri)' : ' (Mon–Sun)';
    if (id === 'year') return ' (YTD)';
    return '';
  };

  const custom = selection.preset === 'custom';

  // ⚠ Spread-and-replace rather than mutate: `selection` is the state object,
  // and editing one endpoint must not drop the other.
  const setCustom = (patch: Partial<RangeSelection>) =>
    setRange({ ...selection, ...patch, preset: 'custom' });

  return (
    <div className="range-controls">
      {/* ⚠ RADIOS, not a <select>. The old client shows all seven presets as a
          visible list, and a side-by-side comparison made the reason obvious: a
          collapsed dropdown hides which scope is active behind a click, on the
          one control that silently scopes every number in the app. The hints
          ("Mon–Fri", "YTD") are only legible in a list too. */}
      <div className="filter-opts" role="radiogroup" aria-label="Date range">
        {RANGE_PRESETS.map((id) => {
          const on = selection.preset === id;
          return (
            <label key={id} className={`filter-opt${on ? ' on' : ''}`}>
              <input
                type="radio"
                name="range-preset"
                value={id}
                checked={on}
                // ⚠ Switching AWAY from custom keeps the stored dates rather
                // than clearing them. Coming back to Custom having lost what you
                // typed is the kind of small hostility that makes a filter feel
                // unreliable.
                onChange={() => setRange({ ...selection, preset: id as RangePreset })}
              />
              <span className="filter-opt-label">
                {LABELS[id]}
                {hint(id) ? <span className="muted">{hint(id)}</span> : null}
              </span>
            </label>
          );
        })}
      </div>

      {/* Revealed only under Custom, directly beneath its radio, as the old
          client does — two date boxes permanently on show read as a second,
          competing control. */}
      {custom ? (
        <div className="range-custom">
          <input
            type="date"
            aria-label="Range start"
            value={selection.customStart ?? ''}
            onChange={(e) => setCustom({ customStart: e.target.value })}
          />
          <span aria-hidden="true">→</span>
          <input
            type="date"
            aria-label="Range end"
            value={selection.customEnd ?? ''}
            onChange={(e) => setCustom({ customEnd: e.target.value })}
          />
          {/* The resolved label. A half-filled custom range is unbounded on the
              missing side, and that is worth stating rather than inferring. */}
          <span className="range-picker-current muted">{label}</span>
        </div>
      ) : null}
    </div>
  );
}
